import React, { createContext, useContext } from 'react';
import ApiClient from '../../lib/api-client';
import { IApplicationUpdateDTO } from '@common-frontend-submodule/interfaces/application/iapplication-update.interface';
import { IGetApplicationsRequestDTO } from '@common-frontend-submodule/interfaces/application/iget-applications-request.interface';
import { IApplicationDTO } from '@common-frontend-submodule/interfaces/application/iapplication.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { ApplicationRoutes } from '@common-frontend-submodule/enums/routes/application-routes.enum';
import { IApplicationCreationDTO } from '@common-frontend-submodule/interfaces/application/iapplication-creation.interface';
import { ISendAssignmentApplicationDTO } from '@common-frontend-submodule/interfaces/application/isend-assignment-application.interface'
import { ISendChatFileApplicationDTO } from '@common-frontend-submodule/interfaces/application/isend-chat-file-application.interface'
import { ISendRequisiteApplicationDTO } from '@common-frontend-submodule/interfaces/application/isend-requisite-application.interface'
import { ISendContractTemplateApplicationDTO } from '@common-frontend-submodule/interfaces/application/isend-contract-template-application.interface'
import { IParseDocumentApplicationDTO } from '@common-frontend-submodule/interfaces/application/iparse-document-application.interface'
import { IToWorkApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-work-application.interface'
import { ISayHelloApplicationDTO } from '@common-frontend-submodule/interfaces/application/isay-hello-application.interface'
import { IToNewApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-new-application.interface'
import { IToContractNegotiationApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-contract-negotiation-application.interface'
import { IToContractSigningApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-contract-signing-application.interface'
import { IToInvoiceNegotiationApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-invoice-negotiation-application.interface'
import { IToAssignmentNegotiationApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-assignment-negotiation-application.interface'
import { IToAssignmentSigningApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-assignment-signing-application.interface'
import { IToAwaitingPaymentsApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-awaiting-payments-application.interface'
import { IToAssignmentExecutionApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-assignment-execution-application.interface'
import { IToAwaitingConfirmationApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-awaiting-confirmation-application.interface'
import { IToActSigningApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-act-signing-application.interface'
import { IToActSignedApplicationDTO } from '@common-frontend-submodule/interfaces/application/ito-act-signed-application.interface'
import { ISendInvoiceRequirementsApplicationDTO } from '@common-frontend-submodule/interfaces/application/isend-invoice-requirements-application.interface'

const apiClient = ApiClient.getInstance();

interface ApplicationContextProps {
  getById: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>;
  allAssignments: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO[]>>;
  filter: (model: IGetApplicationsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IApplicationDTO>>>;
  create: (model: IApplicationCreationDTO) => Promise<IApiResponseGenericDTO<IApplicationDTO>>;
  update: (id: string, model: IApplicationUpdateDTO) => Promise<IApiResponseGenericDTO<IApplicationDTO>>;
  delete: (id: string) => Promise<IApiResponseGenericDTO<boolean>>;
  toWork: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  sayHi: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  sendRequisites: (model: ISendRequisiteApplicationDTO) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toContractNegotiation: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  sendContractTemplate: (model: ISendContractTemplateApplicationDTO) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toContractSignIn: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toInvoiceNegotiation: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toAssignmentSigning: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toAssignmentNegotiation: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toAwaitingPayments: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toAssignmentExecution: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toAwaitingConfirmation: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toActSigned: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toActSignIn: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  parseDoc: (model: IParseDocumentApplicationDTO) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  sendInvoiceRequirements: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  toNewApplication: (id: string) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  sendChatFile: (model: ISendChatFileApplicationDTO) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  sendChatFileLink: (model: ISendChatFileApplicationDTO) => Promise<void>
  sendAssignment: (model: ISendAssignmentApplicationDTO) => Promise<IApiResponseGenericDTO<IApplicationDTO>>
  generateExcel: (model: IGetApplicationsRequestDTO) => Promise<void>;
  getAllClientApplications: (clientId: string) => Promise<IApiResponseGenericDTO<IApplicationDTO[]>>;
}

const ApplicationContext = createContext<ApplicationContextProps | undefined>(undefined);

export const ApplicationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getById = async (accountId: string) => {
    return await apiClient.getRequest<IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${accountId}`);
  };

  const allAssignments = async (id: string) => {
    return await apiClient.getRequest<IApplicationDTO[]>(`${ApplicationRoutes.BasePrefix}/all/${id}`);
  };

  const filter = async (model: IGetApplicationsRequestDTO) => {
    return await apiClient.postRequest<IGetApplicationsRequestDTO, IPaginatedResultDTO<IApplicationDTO>>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.List}`, model);
  };

  const create = async (model: IApplicationCreationDTO) => {
    return await apiClient.postRequest<IApplicationCreationDTO, IApplicationDTO>(ApplicationRoutes.BasePrefix, model);
  };

  const update = async (id: string, model: IApplicationUpdateDTO) => {
    return await apiClient.putRequest<IApplicationUpdateDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${id}`, model);
  };

  const deleteAccount = async (id: string) => {
    return await apiClient.deleteRequest<null, boolean>(`${ApplicationRoutes.BasePrefix}/${id}`);
  };

  const toWork = async (id: string) => {
    return await apiClient.postRequest<IToWorkApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToWork}`, {
      "applicationId": id
    });
  }

  const sayHi = async (id: string) => {
    return await apiClient.postRequest<ISayHelloApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.SayHello}`, {
      "applicationId": id
    });
  }

  const sendRequisites = async (model: ISendRequisiteApplicationDTO) => {
    return await apiClient.postRequest<ISendRequisiteApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.SendRequisite}`, model);
  }

  const sendContractTemplate = async (model: ISendContractTemplateApplicationDTO) => {
    return await apiClient.postRequest<ISendContractTemplateApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.SendContractTemplate}`, model);
  }

  const toNewApplication = async (id: string) => {
    return await apiClient.postRequest<IToNewApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToNew}`, {
      "applicationId": id
    });
  }

  const toContractNegotiation = async (id: string) => {
    return await apiClient.postRequest<IToContractNegotiationApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToContractNegotiation}`, {
      "applicationId": id
    });
  }

  const toContractSignIn = async (id: string) => {
    return await apiClient.postRequest<IToContractSigningApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToContractSigning}`, {
      "applicationId": id
    });
  }

  const toInvoiceNegotiation = async (id: string) => {
    return await apiClient.postRequest<IToInvoiceNegotiationApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToInvoiceNegotiation}`, {
      "applicationId": id
    });
  }

  const toAssignmentNegotiation = async (id: string) => {
    return await apiClient.postRequest<IToAssignmentNegotiationApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToAssignmentNegotiation}`, {
      "applicationId": id
    });
  }

  const toAssignmentSigning = async (id: string) => {
    return await apiClient.postRequest<IToAssignmentSigningApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToAssignmentSigning}`, {
      "applicationId": id
    });
  }

  const toAwaitingPayments = async (id: string) => {
    return await apiClient.postRequest<IToAwaitingPaymentsApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToAwaitingPayments}`, {
      "applicationId": id
    });
  }

  const toAssignmentExecution = async (id: string) => {
    return await apiClient.postRequest<IToAssignmentExecutionApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToAssignmentExecution}`, {
      "applicationId": id
    });
  }

  const toAwaitingConfirmation = async (id: string) => {
    return await apiClient.postRequest<IToAwaitingConfirmationApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToAwaitingConfirmation}`, {
      "applicationId": id
    });
  }

  const toActSignIn = async (id: string) => {
    return await apiClient.postRequest<IToActSigningApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToActSigning}`, {
      "applicationId": id
    });
  }

  const toActSigned = async (id: string) => {
    return await apiClient.postRequest<IToActSignedApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ToActSigned}`, {
      "applicationId": id
    });
  }

  const sendInvoiceRequirements = async (id: string) => {
    return await apiClient.postRequest<ISendInvoiceRequirementsApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.SendInvoiceRequirements}`, {
      "applicationId": id
    });
  }

  const parseDoc = async (model: IParseDocumentApplicationDTO) => {
    return await apiClient.postRequest<IParseDocumentApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.ParseDocument}`, model);
  }

  const sendAssignment = async (model: ISendAssignmentApplicationDTO) => {
    return await apiClient.postRequest<ISendAssignmentApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.SendAssignment}`, model);
  }

  const sendChatFile = async (model: ISendChatFileApplicationDTO) => {
    return await apiClient.postRequest<ISendChatFileApplicationDTO, IApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.SendChatFile}`, model);
  }

  const generateExcel = async (model: IGetApplicationsRequestDTO) => {
    return await apiClient.postBlob<IGetApplicationsRequestDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.GenerateExcel}`, model);
  };

  const getAllClientApplications = async (clientId: string) => {
    return await apiClient.getRequest<IApplicationDTO[]>(`${ApplicationRoutes.BasePrefix}/all-by-client/${clientId}`);
  }

  const sendChatFileLink = async (model: ISendChatFileApplicationDTO) => {
    return await apiClient.postBlob<ISendChatFileApplicationDTO>(`${ApplicationRoutes.BasePrefix}/${ApplicationRoutes.SendChatFileLink}`, model, {}, 'signed_document.pdf');
  };

  return (
    <ApplicationContext.Provider value={{ sendChatFileLink, getAllClientApplications, generateExcel, sendAssignment, toAssignmentSigning: toAssignmentSigning, sendChatFile, toNewApplication, sendInvoiceRequirements, parseDoc, toActSigned, toActSignIn, toAwaitingConfirmation, toAssignmentExecution, toAwaitingPayments, toAssignmentNegotiation, toInvoiceNegotiation, toContractSignIn, sendContractTemplate: sendContractTemplate, toContractNegotiation, sendRequisites, sayHi, toWork, getById, allAssignments: allAssignments, filter, create, update, delete: deleteAccount }}>
      {children}
    </ApplicationContext.Provider>
  );
};

export const useApplicationStore = () => {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error('useApplicationStore must be used within an ApplicationProvider');
  }
  return context;
};
